import React, { useContext, useEffect, useRef, useState } from 'react';
import { Volume2, Plus, BookMarked, Mic, PanelTopClose, XCircle, ArrowUpCircle, CheckCircle, Flag, Lightbulb, Info, Sparkles, Gauge } from 'lucide-react';
import { DictionaryResponse } from '../services/dictionary';
import { UserContext } from '../store/useStore';
import { useNavigate } from 'react-router-dom';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { translateText } from '../hooks/useTranslation';
import { WordPopover } from './WordPopover';
import { getWordNuance, getPartOfSpeechTranslations } from '../data/wordNuances';
import { getBrazilianPhoneticGuide, BrazilianPhoneticGuide, PhonemeInfo } from '../utils/brazilianPhonetics';
import { translateGrammaticalCrossReference, isGrammaticalCrossReference } from '../utils/grammarTranslation';

interface WordDefinitionProps {
  data: DictionaryResponse;
  onSave: () => void;
  isSaving?: boolean;
  translated?: string;
  wordSaved: boolean
  setWordSaved: (saved: boolean) => void
  themeName?: string
  setWordData?: (data: DictionaryResponse | null) => void
  hideClose?: boolean
  hidePractice?: boolean
  onFlagTranslation?: () => void
  isExpression?: boolean
}

interface ClickableDefinitionTextProps {
  text: string;
  onWordClick: (word: string, event: React.MouseEvent) => void;
  className?: string;
}

function ClickableDefinitionText({ text, onWordClick, className = '' }: ClickableDefinitionTextProps) {
  if (!text) return null;
  const words = text.split(' ');

  return (
    <span className={className}>
      {words.map((rawWord, i) => {
        const cleaned = rawWord
          .replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '')
          .replace(/['’]s$/i, '')
          .trim();
        const isClickable = cleaned.length > 0;

        return (
          <React.Fragment key={i}>
            {isClickable ? (
              <span
                onClick={(e) => onWordClick(cleaned, e)}
                className="cursor-pointer hover:underline hover:text-indigo-600 transition-colors"
                title={`Clique para ver detalhes de "${cleaned}"`}
              >
                {rawWord}
              </span>
            ) : (
              <span>{rawWord}</span>
            )}
            {i < words.length - 1 ? ' ' : ''}
          </React.Fragment>
        );
      })}
    </span>
  );
}

// Cache to avoid repeatedly retrying audio URLs that previously failed in the session
const failedAudioUrls = new Set<string>();

export default function WordDefinition({ data, onSave, isSaving = false, translated, wordSaved, setWordSaved, themeName, setWordData, hideClose = false, hidePractice = false, onFlagTranslation, isExpression }: WordDefinitionProps) {
  const { getWord, deleteWord, updateWordTimestamp, getExpression, deleteExpression } = useContext(UserContext);
  const [isHoveringSaved, setIsHoveringSaved] = useState(false);
  const [isAddedToTop, setIsAddedToTop] = useState(false);
  const [isJustSaved, setIsJustSaved] = useState(false);
  const [isFlagged, setIsFlagged] = useState(false);
  const { speak, cancel } = useSpeechSynthesis({ lang: 'en-US' });
  const navigate = useNavigate();
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  
  const [activePopoverWord, setActivePopoverWord] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{top: number, left: number} | null>(null);
  const [speechSpeed, setSpeechSpeed] = useState<1.0 | 0.7>(1.0);
  const [selectedPhoneme, setSelectedPhoneme] = useState<PhonemeInfo | null>(null);
  const [definitionTranslation, setDefinitionTranslation] = useState<string | null>(
    data.definitionTranslation || null
  );

  useEffect(() => {
    if (data.definitionTranslation) {
      setDefinitionTranslation(data.definitionTranslation);
      return;
    }

    const rawPrimaryDef = data.definition || data.meanings?.[0]?.definitions?.[0];
    const defText = typeof rawPrimaryDef === 'string' ? rawPrimaryDef : rawPrimaryDef?.definition;

    if (!defText || typeof defText !== 'string' || defText.trim().length === 0) {
      setDefinitionTranslation(null);
      return;
    }

    // Intercept grammatical inflection stubs to avoid homograph mistranslations (e.g. bear -> urso)
    const grammarTranslation = translateGrammaticalCrossReference(defText);
    if (grammarTranslation) {
      setDefinitionTranslation(grammarTranslation);
      data.definitionTranslation = grammarTranslation;
      return;
    }

    let isCancelled = false;
    translateText(defText.trim(), 'en', 'pt')
      .then((trans) => {
        if (!isCancelled && trans) {
          const clean = trans.trim().replace(/^["']|["']$/g, '');
          const formatted = clean.charAt(0).toUpperCase() + clean.slice(1);
          setDefinitionTranslation(formatted);
          data.definitionTranslation = formatted;
        }
      })
      .catch((err) => {
        console.warn('Could not translate primary definition:', err);
        if (!isCancelled) setDefinitionTranslation(null);
      });

    return () => {
      isCancelled = true;
    };
  }, [data.word, data.definition, data.definitionTranslation]);

  const handleWordClick = (clickedWord: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const estimatedHeight = 350;
    
    let top = rect.bottom + 8;
    if (viewportHeight - rect.bottom < estimatedHeight && rect.top > estimatedHeight) {
      top = Math.max(16, rect.top - estimatedHeight);
    }
    const left = Math.min(Math.max(16, rect.left), window.innerWidth - 300);
    
    setPopoverPosition({ top, left });
    setActivePopoverWord(clickedWord);
  };

  const isExpr = Boolean(isExpression || data.type === 'idiom' || data.type === 'expression' || data.word.trim().includes(' '));

  const stopCurrentAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
  };

  const handleSpeak = (audio: string | null, customRate: number = speechSpeed, accentLang: 'en-US' | 'en-GB' = 'en-US') => {
    const rawAudio = audio?.trim();
    const cleanAudio = rawAudio ? (rawAudio.startsWith('//') ? `https:${rawAudio}` : rawAudio) : null;
    const isUkAudio = cleanAudio ? /(-uk|-gb|_uk|_gb)[._\-\d]*\.(mp3|ogg|wav)($|\?)/i.test(cleanAudio) : false;
    const isFailedUrl = cleanAudio ? failedAudioUrls.has(cleanAudio) : false;
    const shouldUseAudioFile = Boolean(
      cleanAudio && !isFailedUrl && ((accentLang === 'en-US' && !isUkAudio) || (accentLang === 'en-GB' && isUkAudio))
    );

    if (shouldUseAudioFile && cleanAudio) {
      cancel();
      stopCurrentAudio();
      const nextAudio = new Audio(cleanAudio);
      nextAudio.playbackRate = customRate;
      currentAudioRef.current = nextAudio;

      let hasFallenBack = false;
      const fallbackToSpeech = (err?: any) => {
        if (hasFallenBack) return;
        hasFallenBack = true;
        if (cleanAudio) {
          failedAudioUrls.add(cleanAudio);
        }
        if (currentAudioRef.current === nextAudio) {
          currentAudioRef.current = null;
        }
        stopCurrentAudio();
        speak(data.word, accentLang, customRate);
      };

      nextAudio.onended = () => {
        if (currentAudioRef.current === nextAudio) {
          currentAudioRef.current = null;
        }
      };

      nextAudio.onerror = (e) => {
        console.warn('Phonetic audio error event, falling back to speech synthesis:', e);
        fallbackToSpeech(e);
      };

      nextAudio.play().catch((error) => {
        console.warn('Error playing phonetic audio, falling back to speech synthesis:', error);
        fallbackToSpeech(error);
      });
    } else {
      stopCurrentAudio();
      speak(data.word, accentLang, customRate);
    }
  };

  const handleGetWord = async () => {
    let wordExists = false;
    if (isExpr && getExpression) {
      const exprData = await getExpression(data.word);
      if (exprData) wordExists = true;
    }
    if (!wordExists && getWord) {
      const wordData = await getWord(data.word);
      if (wordData) wordExists = true;
    }
    setWordSaved(wordExists);
    setIsAddedToTop(false);
  };

  const handleDelete = async () => {
    if (isExpr && deleteExpression) {
      await deleteExpression(data.word);
    } else if (deleteWord) {
      await deleteWord(data.word);
    }
    setWordSaved(false);
  };

  const handleAddToTop = async () => {
    await updateWordTimestamp(data.word);
    setIsAddedToTop(true);
  };

  useEffect(() => {
    if(data) {
      handleGetWord()
      setIsJustSaved(false);
      setIsFlagged(false);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      stopCurrentAudio();
      cancel();
    };
  }, [cancel]);

  const showExample = (example: string | undefined | null) => {
    if(example) {
      return (
        <div className="flex items-center space-x-3 mt-2">
          <p className="text-gray-600 mt-1 italic">
            <span className="font-semibold not-italic">Example:</span> "
            <ClickableDefinitionText text={example} onWordClick={handleWordClick} />
            "
          </p>
        </div>
      )
    }
    return null;
  }

  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 sm:gap-6 mb-6">
        <div className="flex-1 min-w-0">
          {data.type && data.type !== 'single_word' && (
            <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide mb-2">
              {data.type.replace('_', ' ')}
            </span>
          )}
          <h2 className="text-3xl font-bold text-gray-900 pb-2 pr-4">{data.word}</h2>
          <div className="flex items-center gap-2 mt-2 pr-4">
            <label className="text-gray-600 font-bold">Tradução (Real Meaning):</label>
            {onFlagTranslation && translated && (
              <button 
                onClick={() => {
                  if (!isFlagged) {
                    setIsFlagged(true);
                    onFlagTranslation();
                  }
                }} 
                disabled={isFlagged}
                className={`transition-colors flex items-center justify-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${
                  isFlagged 
                    ? 'text-red-600 bg-red-50 border-red-200' 
                    : 'text-gray-500 bg-white border-gray-200 hover:text-red-600 hover:bg-red-50 hover:border-red-200'
                }`}
                title={isFlagged ? "Translation flagged as incorrect" : "Flag incorrect translation"}
              >
                <Flag className="w-3.5 h-3.5" />
                <span>{isFlagged ? "Reported" : "Report Error"}</span>
              </button>
            )}
          </div>
          {translated && (
            <div className="pr-4 pb-2">
              <p id="translated" data-testid="translated" className="text-gray-900 font-medium mt-1">
                {translated}
              </p>
            </div>
          )}
          {definitionTranslation && (
            <div className="pr-4 pb-3">
              <p className="text-gray-600 text-sm mt-0.5 flex items-start gap-1.5" data-testid="definition-translation">
                <span className="font-semibold text-gray-700 shrink-0">Significado:</span>
                <span>"{definitionTranslation}"</span>
              </p>
            </div>
          )}
          
          {data.literalMeaning && (
            <>
              <label className="text-gray-600 mt-2 font-bold pr-4">Literal Meaning:</label>
              <p className="text-gray-600 mt-1 pr-4 pb-3">{data.literalMeaning}</p>
            </>
          )}
          {!translated && (
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" />
            </div>
          )}
        </div>
        <div className='flex flex-col gap-2 w-full sm:w-44 shrink-0'>
          <button
            onClick={() => {
              if (wordSaved) {
                handleDelete();
              } else {
                setIsJustSaved(true);
                onSave();
              }
            }}
            disabled={isSaving && !wordSaved}
            onMouseEnter={() => setIsHoveringSaved(true)}
            onMouseLeave={() => setIsHoveringSaved(false)}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-colors disabled:opacity-50 ${
              wordSaved 
                ? isHoveringSaved 
                  ? 'bg-red-500 text-white border border-red-500 hover:bg-red-600 hover:border-red-600' 
                  : 'bg-white text-purple-600 border border-purple-600' 
                : 'bg-white text-purple-600 border border-purple-600 hover:bg-purple-200'
            } duration-300`}
          >
            {isSaving && !wordSaved ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />
            ) : (
              wordSaved ? 
              <>
                {isHoveringSaved ? <XCircle className="w-5 h-5 shrink-0" /> : <BookMarked className="w-5 h-5 shrink-0" />}
                <span data-testid='word-saved' className="whitespace-nowrap">{isHoveringSaved ? 'Unsave' : 'Word Saved'}</span> 
              </>
              : 
              <>
                <Plus className="w-5 h-5 shrink-0" />
                <span data-testid='save-word' className="whitespace-nowrap">Save Word</span>
              </>
            )}
          </button>
          
          {wordSaved && !isJustSaved && (
            <div className="relative group flex items-center w-full">
              <button
                onClick={handleAddToTop}
                disabled={isAddedToTop}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 bg-white text-purple-600 border border-purple-600 rounded-lg whitespace-nowrap text-sm font-medium transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${!isAddedToTop ? 'hover:bg-purple-50' : ''}`}
                data-testid='add-to-top'
              >
                {isAddedToTop ? <CheckCircle className="w-4 h-4 shrink-0" /> : <ArrowUpCircle className="w-4 h-4 shrink-0" />}
                <span className="whitespace-nowrap">{isAddedToTop ? 'Added to the top' : 'Add to the top'}</span>
              </button>
              {!isAddedToTop && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-48 text-center z-10 pointer-events-none">
                  this will put the saved word in the most recent saved words so the person can study it more
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-b-gray-900"></div>
                </div>
              )}
            </div>
          )}
          { !hidePractice ? (<button
                  onClick={() => navigate('/speaking', { state: { word: data.word, themeName: themeName } })}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-400 whitespace-nowrap text-sm font-medium transition-colors duration-300"
                  data-testid='practice-word'
                >
                  <Mic className="w-4 h-4 shrink-0" />
                  <span>Practice</span>
          </button>) : null }
          {!hideClose && <button
                  onClick={() => setWordData(null)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-200 whitespace-nowrap text-sm font-medium transition-colors duration-300"
                  data-testid='close-word'
                >
                  <XCircle className="w-4 h-4 shrink-0" />
                  <span>Close</span>
          </button> }
        </div>
      </div>

      {/* PRONÚNCIA OFICIAL & GUIA BRASILEIRO */}
      {(() => {
        const bestIpa = data.phonetic || data.phonetics?.find(p => p.text && p.text.trim().length > 0)?.text || '';
        const audioUrl = data.phonetics?.find(p => p.audio && p.audio.trim().length > 0)?.audio || data.audio || null;
        const usAudioUrl =
          data.phonetics?.find(p => p.audio && /(-us|_us)[._\-\d]*\.(mp3|ogg|wav)($|\?)/i.test(p.audio))?.audio ||
          (audioUrl && !/(-uk|-gb|_uk|_gb)[._\-\d]*\.(mp3|ogg|wav)($|\?)/i.test(audioUrl) ? audioUrl : null);
        const ukAudioUrl =
          data.phonetics?.find(p => p.audio && /(-uk|-gb|_uk|_gb)[._\-\d]*\.(mp3|ogg|wav)($|\?)/i.test(p.audio))?.audio ||
          (audioUrl && /(-uk|-gb|_uk|_gb)[._\-\d]*\.(mp3|ogg|wav)($|\?)/i.test(audioUrl) ? audioUrl : null);
        const brGuide = getBrazilianPhoneticGuide(data.word, bestIpa);

        return (
          <div className="mb-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="phonetic-text" className="text-base font-bold text-gray-800 flex items-center gap-1.5">
                <Volume2 className="w-4 h-4 text-indigo-600" />
                Pronúncia Oficial (API)
              </label>
              
              {/* Speed toggle */}
              <button
                type="button"
                onClick={() => setSpeechSpeed(speechSpeed === 1.0 ? 0.7 : 1.0)}
                className={`px-2 py-0.5 rounded text-xs font-semibold border flex items-center gap-1 transition-colors ${
                  speechSpeed === 0.7 
                    ? 'bg-amber-100 text-amber-800 border-amber-300' 
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
                title="Velocidade da pronúncia"
              >
                <Gauge className="w-3 h-3" />
                <span>{speechSpeed}x</span>
              </button>
            </div>

            {/* API Official Pronunciation Row */}
            <div className="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl p-2.5 mb-3 flex-wrap">
              <span id="phonetic-text" data-testid="phonetic-text" className="text-sm font-mono font-bold text-indigo-900 bg-white px-2.5 py-1 rounded-md border border-slate-200">
                {bestIpa || brGuide.ipa || data.word}
              </span>

              <button
                id="phonetic-audio"
                data-testid="phonetic-audio"
                type="button"
                onClick={() => handleSpeak(usAudioUrl, speechSpeed, 'en-US')}
                className="p-1.5 px-3 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-semibold shadow-xs"
                title="Ouvir pronúncia americana (US)"
              >
                <Volume2 className="w-4 h-4" />
                <span>🇺🇸 US ({speechSpeed}x)</span>
              </button>

              <button
                id="phonetic-audio-uk"
                data-testid="phonetic-audio-uk"
                type="button"
                onClick={() => handleSpeak(ukAudioUrl, speechSpeed, 'en-GB')}
                className="p-1.5 px-3 bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-semibold shadow-xs"
                title="Ouvir pronúncia britânica (UK)"
              >
                <Volume2 className="w-4 h-4" />
                <span>🇬🇧 UK ({speechSpeed}x)</span>
              </button>
            </div>

            {/* GUIA FONÉTICO PARA BRASILEIROS */}
            <div className="bg-gradient-to-br from-purple-50 via-indigo-50/40 to-white rounded-xl p-3.5 border border-purple-200 mb-3 shadow-2xs">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-extrabold uppercase tracking-wider text-purple-900 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                  Como Falar em Português
                </span>
                <span className="text-[11px] font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
                  Fonética Brazuca
                </span>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-600">Pronuncie como:</span>
                <span className="text-base font-black text-purple-800 bg-white px-3 py-1 rounded-lg border border-purple-300 shadow-2xs tracking-wide">
                  "{brGuide.respelling}"
                </span>
              </div>

              {brGuide.generalTip && (
                <p className="text-xs text-purple-900 font-medium mb-2 bg-purple-100/50 p-2 rounded-md">
                  💡 {brGuide.generalTip}
                </p>
              )}

              {/* KEY PHONEMES WITH EXPLANATIONS */}
              {brGuide.keyPhonemes.length > 0 && (
                <div className="mt-2.5 pt-2 border-t border-purple-100">
                  <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider block mb-1.5">
                    Fonemas-Chave para Brasileiros:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {brGuide.keyPhonemes.map((ph, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => setSelectedPhoneme(selectedPhoneme?.symbol === ph.symbol ? null : ph)}
                        className="bg-white px-2.5 py-1 rounded-lg border border-purple-200 text-xs cursor-pointer hover:border-purple-400 hover:shadow-2xs transition-all flex items-center gap-1.5"
                        title="Clique para ver como posicionar a boca"
                      >
                        <span className="font-mono font-bold text-purple-700">{ph.symbol}</span>
                        <span className="text-gray-600 text-[11px] font-medium">{ph.name}</span>
                      </div>
                    ))}
                  </div>

                  {/* Expanded Phoneme Details */}
                  {selectedPhoneme && (
                    <div className="mt-2 p-2.5 bg-white rounded-lg border border-purple-300 text-xs shadow-xs transition-all">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-bold text-purple-900">
                          {selectedPhoneme.symbol} - {selectedPhoneme.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedPhoneme(null)}
                          className="text-gray-400 hover:text-gray-600 p-0.5"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-gray-700 mb-1 leading-relaxed">
                        {selectedPhoneme.portugueseExplanation}
                      </p>
                      <div className="text-[11px] text-purple-800 bg-purple-50 p-1.5 rounded font-medium">
                        👄 <strong>Posição da boca:</strong> {selectedPhoneme.mouthTip}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* BRAZILIAN PRONUNCIATION TRAPS */}
              {brGuide.traps.length > 0 && (
                <div className="mt-2.5 pt-2 border-t border-purple-100">
                  <span className="text-[11px] font-bold text-amber-900 uppercase tracking-wider block mb-1">
                    ⚠️ Atenção Brasileiros:
                  </span>
                  <ul className="space-y-1">
                    {brGuide.traps.slice(0, 3).map((trap, idx) => (
                      <li key={idx} className="text-xs text-amber-950 flex items-start gap-1.5 leading-snug">
                        <span className="text-amber-600 font-bold">•</span>
                        <span>{trap}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Nuance & Cultural Notes Section */}
      {(() => {
        const nuance = data.nuance || getWordNuance(data.word);
        if (!nuance) return null;
        return (
          <div className="mb-6 p-5 rounded-xl border border-amber-200 bg-amber-50/70 shadow-sm" data-testid="word-nuance-box">
            <div className="flex items-center gap-2 mb-2">
              <span className="p-1.5 rounded-lg bg-amber-100 text-amber-800">
                <Lightbulb className="w-5 h-5 text-amber-700" />
              </span>
              <h3 className="text-base font-bold text-amber-900">
                {nuance.title}
              </h3>
              <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">
                Nuance Note
              </span>
            </div>
            
            <p className="text-sm text-gray-700 mb-4 leading-relaxed">
              {nuance.summary}
            </p>

            {nuance.distinctions && nuance.distinctions.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {nuance.distinctions.map((dist, idx) => (
                  <div key={idx} className="bg-white p-3.5 rounded-lg border border-amber-100 shadow-xs">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">{dist.term}</span>
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                        {dist.translation}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-normal">{dist.explanation}</p>
                    {dist.example && (
                      <p className="text-xs italic text-gray-500 mt-2 border-t border-gray-100 pt-1.5">
                        "{dist.example}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {nuance.commonUsages && nuance.commonUsages.length > 0 && (
              <div>
                <span className="text-xs font-bold text-amber-900 uppercase tracking-wider block mb-2">
                  Usos além de animais (lost / stray):
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {nuance.commonUsages.map((use, idx) => (
                    <div key={idx} className="flex items-start justify-between bg-white/80 p-2.5 rounded border border-amber-100 text-xs">
                      <div>
                        <span className="font-semibold text-gray-800 block">{use.phrase}</span>
                        <span className="text-gray-500 text-[11px]">{use.context}</span>
                      </div>
                      <span className="text-indigo-600 font-medium pl-2 text-right">{use.translation}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="space-y-6">
        {data.meanings.map((meaning, index) => {
          const posMap = getPartOfSpeechTranslations(data.word);
          const posTranslations = posMap ? posMap[meaning.partOfSpeech.toLowerCase()] : null;

          return (
            <div key={index}>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <h3 className="text-lg font-semibold text-gray-800 capitalize">
                  {meaning.partOfSpeech}
                </h3>
                {posTranslations && (
                  <span
                    data-testid={`pos-badge-${meaning.partOfSpeech.toLowerCase()}`}
                    className="text-xs font-medium text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100"
                  >
                    {posTranslations.join(' • ')}
                  </span>
                )}
              </div>
              <div className="space-y-4">
                {meaning?.definitions?.map((def, defIndex) => (
                  <div key={defIndex} className="pl-4 border-l-2 border-gray-200">
                    <p className="text-gray-700">
                      <ClickableDefinitionText
                        text={typeof def === 'string' ? def : def.definition}
                        onWordClick={handleWordClick}
                      />
                    </p>
                    {defIndex === 0 && index === 0 && definitionTranslation && (
                      <p className="text-gray-500 text-sm mt-1 italic" data-testid="primary-definition-translation">
                        <span className="font-semibold not-italic">Tradução:</span> "{definitionTranslation}"
                      </p>
                    )}
                    {!(defIndex === 0 && index === 0) && isGrammaticalCrossReference(typeof def === 'string' ? def : def.definition) && (
                      <p className="text-gray-500 text-sm mt-1 italic" data-testid="grammatical-definition-translation">
                        <span className="font-semibold not-italic">Tradução:</span> "{translateGrammaticalCrossReference(typeof def === 'string' ? def : def.definition)}"
                      </p>
                    )}
                    {def.examples && def.examples.length > 0 ? (
                      def.examples.filter((example) => example !== null && example !== undefined && example !== '').map((example, exampleIndex) => (
                        <div key={exampleIndex} className="mt-2">
                          <p className="text-gray-600 mt-1 italic">
                            <span className="font-semibold not-italic">Example:</span> "
                            <ClickableDefinitionText
                              text={example}
                              onWordClick={handleWordClick}
                            />
                            "
                          </p>
                          {data.exampleTranslation && exampleIndex === 0 && (
                            <p className="text-gray-500 text-sm mt-1">
                              <span className="font-semibold">Translation:</span> {data.exampleTranslation}
                            </p>
                          )}
                        </div>
                      ))
                    ) : (
                      showExample(def?.example)
                    )}
                    {def?.synonyms?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1 items-center">
                        <span className="text-sm text-gray-500">Synonyms: </span>
                        {def.synonyms.map((synonym, sIndex) => (
                          <React.Fragment key={sIndex}>
                            <button
                              onClick={(e) => handleWordClick(synonym, e)}
                              className="text-sm text-indigo-600 hover:underline"
                            >
                              {synonym}
                            </button>
                            {sIndex < def.synonyms.length - 1 && <span className="text-sm text-indigo-600">, </span>}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {activePopoverWord && popoverPosition && (
        <WordPopover
          word={activePopoverWord}
          isExpression={activePopoverWord.includes(' ')}
          onClose={() => {
            setActivePopoverWord(null);
            setPopoverPosition(null);
          }}
          position={popoverPosition}
        />
      )}
    </div>
  );
}